<template>
  <div class="max-w-full mx-auto px-6 py-6 dark:bg-gray-600 dark:border-gray-600">
    <div
      class="text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700">
      <ul class="flex flex-wrap -mb-px">
        <li class="me-2">
          <button @click="changeTab('users')" class="inline-block p-4 border-b-2 border-transparent rounded-t-lg"
            :class="activeTab === 'users'
              ? 'text-yellow-600 border-yellow-600 dark:text-yellow-500 dark:border-yellow-500'
              : 'hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'">
            Users ({{ usersTotal }})
          </button>
        </li>
        <li class="me-2">
          <button @click="changeTab('inventory')" class="inline-block p-4 border-b-2 border-transparent rounded-t-lg"
            :class="activeTab === 'inventory'
              ? 'text-yellow-600 border-yellow-600 dark:text-yellow-500 dark:border-yellow-500'
              : 'hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'">
            Devices ({{ inventoryTotal }})
          </button>
        </li>
      </ul>
    </div>

    <div class="mt-6 p-4 bg-gray-100 rounded-md dark:bg-gray-800 shadow-md">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12 lg:col-span-9">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Company</label>
              <select v-model="companyId" @change="onCompanyChange"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg py-2 px-3 bg-white dark:bg-gray-700 dark:text-gray-100 text-left">
                <option :value="null" disabled>Select a company...</option>
                <option v-for="c in clients" :key="c.id" :value="c.id">
                  {{ c.companyname }}
                </option>
              </select>
            </div>

            <div v-if="activeTab === 'users'">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Users Status</label>
              <select v-model="filters.users.status"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg py-2 px-3 bg-white dark:bg-gray-700 dark:text-gray-100">
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div v-if="activeTab === 'inventory'">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Device Status</label>
              <select v-model="filters.inventory.status"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg py-2 px-3 bg-white dark:bg-gray-700 dark:text-gray-100">
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div v-if="activeTab === 'users'">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">User Type</label>
              <select v-model="filters.users.user_type"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg py-2 px-3 bg-white dark:bg-gray-700 dark:text-gray-100">
                <option value="all">All</option>
                <option value="cloud">Cloud</option>
                <option value="standard">Standard</option>
                <option value="non-billable">non-billable</option>
              </select>
            </div>
          </div>

          <div class="flex flex-wrap mt-4">
            <span v-for="chip in activeFilterChips" :key="chip.key"
              class="bg-yellow-300 text-black px-3 py-1 rounded-full text-sm flex items-center m-1">
              {{ chip.label }}
              <button @click="clearChip(chip.key)" class="ml-2 text-gray-700 hover:text-red-500">✕</button>
            </span>
          </div>
        </div>
        <div v-if="activeTab === 'users'" class="col-span-12 lg:col-span-3">
          <div class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 shadow-sm">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-100">User Type Stats</h3>
              <span class="text-xs text-gray-500 dark:text-gray-300">
                Total
              </span>
            </div>

            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-700 dark:text-gray-200">Standard</span>
                <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ userTypeStats.standard }}</span>
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-700 dark:text-gray-200">Cloud</span>
                <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ userTypeStats.cloud }}</span>
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-700 dark:text-gray-200">Non-billable</span>
                <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ userTypeStats.non_billable
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'inventory'" class="col-span-12 lg:col-span-3">
          <div class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 shadow-sm">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-100">Device Type Stats</h3>
              <span class="text-xs text-gray-500 dark:text-gray-300">
                Total
              </span>
            </div>

            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-700 dark:text-gray-200">Workstation</span>
                <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ deviceTypeStats.workstation
                }}</span>
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-700 dark:text-gray-200">Server</span>
                <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ deviceTypeStats.server }}</span>
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-700 dark:text-gray-200">MAC</span>
                <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ deviceTypeStats.mac }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div class="bg-gray-100 dark:bg-gray-800 relative shadow-md sm:rounded-lg overflow-hidden mt-6">
      <div class="flex flex-col md:flex-row items-center justify-between p-4 gap-3">
        <input type="text" v-model="searchQuery"
          class="w-full md:w-1/3 px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          :placeholder="activeTab === 'users' ? 'Search users...' : 'Search devices...'">
        <button @click="exportToCSV" class="bg-gray-400 hover:bg-yellow-500 text-white px-4 py-2 rounded-md">
          Export
        </button>
      </div>
      <div v-if="activeTab === 'users'" class="overflow-x-auto">
        <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('name_first')">
                  Name <span class="text-xs">{{ sortIcon('name_first') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('email')">
                  Email <span class="text-xs">{{ sortIcon('email') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('user_type')">
                  User Type <span class="text-xs">{{ sortIcon('user_type') }}</span>
                </button>
              </th>

              <th class="px-4 py-3" v-if="companyId != 30">
                <button class="flex items-center gap-2" @click="onSort('azure_license')">
                  Primary License <span class="text-xs">{{ sortIcon('azure_license') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                Source
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('tmstmp')">
                  Updated <span class="text-xs">{{ sortIcon('tmstmp') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('retired')">
                  Active <span class="text-xs">{{ sortIcon('retired') }}</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in rowsForActiveTab" :key="u.id" class="hover:bg-gray-100 dark:hover:bg-gray-700">
              <td class="px-4 py-2">{{ u.name }}</td>
              <td class="px-4 py-2">{{ u.email }}</td>
              <td class="px-4 py-2">
                <div v-if="canOverrideUserType" class="flex items-center gap-2">
                  <select :value="u.user_type" @change="handleUserTypeChange(u, $event)"
                    class="border border-gray-300 dark:border-gray-600 rounded-lg py-1 px-2 bg-white dark:bg-gray-700 dark:text-gray-100 text-sm"
                    :class="u.user_type_override_permanent == 1 ? 'font-semibold border-green-600' : ''">
                    <option v-for="opt in userTypeChoices" :key="opt.value" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>

                  <span v-if="u.user_type_overridden == 1" class="text-xs italic text-red-500">
                    manual
                  </span>
                </div>

                <div v-else>
                  {{ u.user_type }}
                  <span v-if="u.user_type_overridden == 1">(manual)</span>
                </div>
              </td>
              <td class="px-4 py-2" v-if="companyId != 30">{{ u.azure_license }}</td>
              <td class="px-4 py-2">
                {{ (u.azure_id && String(u.azure_id).trim() !== '') ? 'Azure' : 'Manual' }}
              </td>
              <td class="px-4 py-2">{{ formatDt(u.tmstmp) }}</td>
              <td class="px-4 py-2">{{ u.retired ? 'Inactive' : 'Active' }}</td>
            </tr>

            <tr v-if="rowsForActiveTab.length === 0">
              <td colspan="7" class="px-4 py-4 text-center text-gray-500 dark:text-gray-300">
                No users found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('system_name')">
                  System <span class="text-xs">{{ sortIcon('system_name') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('node_class')">
                  Type <span class="text-xs">{{ sortIcon('node_class') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('os_name')">
                  OS <span class="text-xs">{{ sortIcon('os_name') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('system_manufacturer')">
                  Manufacturer <span class="text-xs">{{ sortIcon('system_manufacturer') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('system_model')">
                  Model <span class="text-xs">{{ sortIcon('system_model') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('bios_serial_number')">
                  BIOS Serial Number <span class="text-xs">{{ sortIcon('bios_serial_number') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('processor_name')">
                  Processor Name <span class="text-xs">{{ sortIcon('processor_name') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('memory_capacity')">
                  Memory <span class="text-xs">{{ sortIcon('memory_capacity') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('last_logged_in_user')">
                  Last User <span class="text-xs">{{ sortIcon('last_logged_in_user') }}</span>
                </button>
              </th>
              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('location_name')">
                  Location <span class="text-xs">{{ sortIcon('location_name') }}</span>
                </button>
              </th>
              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('last_update_at_ninja')">
                  Last Update <span class="text-xs">{{ sortIcon('last_update_at_ninja') }}</span>
                </button>
              </th>

              <th class="px-4 py-3">
                <button class="flex items-center gap-2" @click="onSort('active')">
                  Active <span class="text-xs">{{ sortIcon('active') }}</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in rowsForActiveTab" :key="d.id" class="hover:bg-gray-100 dark:hover:bg-gray-700">
              <td class="px-4 py-2 whitespace-nowrap">{{ d.system_name || '' }}</td>
              <td class="px-4 py-2">{{ d.node_class || '' }}</td>
              <td class="px-4 py-2">{{ d.os_name || '' }}</td>
              <td class="px-4 py-2">{{ d.system_manufacturer || '' }}</td>
              <td class="px-4 py-2">{{ d.system_model || '' }}</td>
              <td class="px-4 py-2">{{ d.bios_serial_number || '' }}</td>
              <td class="px-4 py-2">{{ d.processor_name || '' }}</td>
              <td class="px-4 py-2">{{ formatMemory(d.memory_capacity) }}</td>
              <td class="px-4 py-2">{{ d.last_logged_in_user || '' }}</td>
              <td class="px-4 py-2">
                <span class="inline-block max-w-[220px] truncate" :title="d.location_address || d.location_name || ''">
                  {{ d.location_name || '' }}
                </span>
              </td>
              <td class="px-4 py-2">{{ formatDt(d.last_update_at_ninja) }}</td>
              <td class="px-4 py-2">{{ d.active ? 'Active' : 'Inactive' }}</td>
            </tr>

            <tr v-if="rowsForActiveTab.length === 0">
              <td colspan="11" class="px-4 py-4 text-center text-gray-500 dark:text-gray-300">
                No Devices
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex justify-end items-center mt-4 px-4 pb-4">
        <button @click="prevPage" :disabled="activePage <= 1"
          class="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500 disabled:opacity-50 mr-4">
          Previous
        </button>

        <span class="text-sm text-gray-700 dark:text-gray-300 mr-2">
          Page {{ activePage }} of {{ activeTotalPages }}
        </span>

        <button @click="nextPage" :disabled="activePage >= activeTotalPages"
          class="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500 disabled:opacity-50">
          Next
        </button>
      </div>
    </div>
    <div v-if="showUserTypeModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div
        class="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Confirm User Type Override
        </h3>

        <p class="mt-3 text-sm text-gray-700 dark:text-gray-300">
          You changed the user type to
          <strong>{{ pendingUserTypeValue }}</strong>.
          Should this override be permanent?
        </p>

        <div class="mt-6 flex flex-wrap gap-3 justify-end">
          <button type="button" @click="confirmUserTypeOverride(true)"
            class="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700">
            Yes (Permanent)
          </button>

          <button type="button" @click="confirmUserTypeOverride(false)"
            class="px-4 py-2 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600">
            No
          </button>

          <button type="button" @click="cancelUserTypeOverride"
            class="px-4 py-2 rounded-lg bg-gray-400 text-white hover:bg-gray-500">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      activeTab: "users",
      userId: localStorage.getItem('user_id'),
      userroles: [60],
      clients: [],
      companyId: null,

      users: [],
      userTypeStats: [],
      deviceTypeStats: [],
      inventory: [],
      usersPage: 1,
      usersPerPage: 1000,
      usersTotalPages: 1,
      usersTotal: 0,

      inventoryPage: 1,
      inventoryPerPage: 1000,
      inventoryTotalPages: 1,
      inventoryTotal: 0,

      pendingUserTypeRow: null,
      pendingUserTypeValue: "",
      pendingUserTypeOldValue: "",
      showUserTypeModal: false,

      searchQuery: "",
      sort: {
        users: { by: "name_first", dir: "asc" },
        inventory: { by: "last_update_at_ninja", dir: "desc" },
      },

      filters: {
        users: {
          status: "active",
          user_type: "all",
        },
        inventory: {
          status: "active",
        },
      },
      _searchTimer: null,
    };
  },

  computed: {
    canOverrideUserType() {
      return this.userroles.some(r => [10, 20, 30].includes(Number(r)));
    },

    userTypeChoices() {
      return [
        { label: "Standard", value: "standard" },
        { label: "Cloud", value: "cloud" },
        { label: "Non-billable", value: "non-billable" },
      ];
    },
    userTypeOptions() {
      const set = new Set();
      this.users.forEach(u => {
        if (u.user_type !== null && u.user_type !== undefined && String(u.user_type).trim() !== "") {
          set.add(String(u.user_type));
        }
      });
      return Array.from(set).sort();
    },

    rowsForActiveTab() {
      return this.activeTab === "users" ? this.users : this.inventory;
    },

    activePage() {
      return this.activeTab === "users" ? this.usersPage : this.inventoryPage;
    },

    activeTotalPages() {
      return this.activeTab === "users"
        ? Math.max(1, this.usersTotalPages)
        : Math.max(1, this.inventoryTotalPages);
    },

    activeFilterChips() {
      const chips = [];

      if (this.companyId) {
        const c = this.clients.find(x => x.id === this.companyId);
        chips.push({ key: "companyId", label: `Company: ${c?.companyname || this.companyId}` });
      }

      if (this.activeTab === "users") {
        if (this.filters.users.status !== "all") {
          chips.push({ key: "users.status", label: `Users: ${this.filters.users.status}` });
        }
        if (this.filters.users.user_type !== "all") {
          chips.push({ key: "users.user_type", label: `User Type: ${this.filters.users.user_type}` });
        }
      } else {
        if (this.filters.inventory.status !== "all") {
          chips.push({ key: "inventory.status", label: `Device Status: ${this.filters.inventory.status}` });
        }
      }

      if (this.searchQuery.trim()) {
        chips.push({ key: "searchQuery", label: `Search: ${this.searchQuery.trim()}` });
      }

      return chips;
    },
    activeSort() {
      return this.activeTab === "users" ? this.sort.users : this.sort.inventory;
    },
  },

  watch: {
    activeTab() {
      this.searchQuery = "";
      this.resetPageForActiveTab();
      this.fetchActiveTab();
    },
    "filters.users.status"() {
      if (this.activeTab !== "users") return;
      this.usersPage = 1;
      this.fetchUsers();
    },
    "filters.users.user_type"() {
      if (this.activeTab !== "users") return;
      this.usersPage = 1;
      this.fetchUsers();
    },
    "filters.inventory.status"() {
      if (this.activeTab !== "inventory") return;
      this.inventoryPage = 1;
      this.fetchInventory();
    },
    searchQuery() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.resetPageForActiveTab();
        this.fetchActiveTab();
      }, 350);
    },
  },

  methods: {
    changeTab(tab) {
      this.activeTab = tab;
    },
    isSorted(col) {
      return this.activeSort.by === col;
    },

    sortIcon(col) {
      if (!this.isSorted(col)) return "↕";
      return this.activeSort.dir === "asc" ? "↑" : "↓";
    },

    async onSort(col) {
      const s = this.activeSort;

      if (s.by === col) {
        s.dir = s.dir === "asc" ? "desc" : "asc";
      } else {
        s.by = col;
        s.dir = this.activeTab === "users" ? "asc" : "desc";
      }

      this.resetPageForActiveTab();
      await this.fetchActiveTab();
    },

    resetPageForActiveTab() {
      if (this.activeTab === "users") this.usersPage = 1;
      else this.inventoryPage = 1;
    },

    async init() {
      this.$updateLoading(1);
      try {
        const [, roleRes] = await Promise.all([
          this.fetchClients(),
          axios.get(`user/role/${this.userId}`),
        ]);
        this.userroles = roleRes.data.roles;
        if (!this.companyId && this.clients.length > 0 && this.userroles.some(r => [60, 70, 80].includes(r))) {
          this.companyId = this.clients[0].id;
        } else {
          this.companyId = 30;
        }
        if (this.companyId) {
          await Promise.all([
            this.fetchUsers(),
            this.fetchInventory(),
          ]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.$updateLoading(-1);
      }
    },

    async fetchClients() {
      const res = await axios.get("clients");
      this.clients = res.data || [];
    },

    async onCompanyChange() {
      this.usersPage = 1;
      this.inventoryPage = 1;
      await Promise.all([
        this.fetchUsers(),
        this.fetchInventory(),
      ]);
    },

    async fetchActiveTab() {
      if (!this.companyId) return;
      if (this.activeTab === "users") return this.fetchUsers();
      return this.fetchInventory();
    },

    async fetchUsers() {
      if (!this.companyId) return;

      const params = {
        page: this.usersPage,
        per_page: this.usersPerPage,
        status: this.filters.users.status,
        user_type: this.filters.users.user_type,
        q: this.searchQuery || "",
        sort_by: this.sort.users.by,
        sort_dir: this.sort.users.dir,
      };

      this.$updateLoading(1);
      try {
        const res = await axios.get(`requesters/paginated/${this.companyId}`, { params });

        this.users = res.data?.data || [];
        this.userTypeStats = res.data?.type_totals || [];
        this.usersPage = res.data?.current_page || 1;
        this.usersTotalPages = res.data?.last_page || 1;
        this.usersTotal = res.data?.total || this.users.length;
      } catch (e) {
        console.error("Error fetching users:", e);
        this.users = [];
        this.usersTotal = 0;
        this.usersTotalPages = 1;
      } finally {
        this.$updateLoading(-1);
      }
    },

    async fetchInventory() {
      if (!this.companyId) return;

      const params = {
        page: this.inventoryPage,
        per_page: this.inventoryPerPage,
        status: this.filters.inventory.status,
        q: this.searchQuery || "",
        sort_by: this.sort.inventory.by,
        sort_dir: this.sort.inventory.dir,
      };

      this.$updateLoading(1);
      try {
        const res = await axios.get(`clients/assets/paginated/${this.companyId}`, { params });

        this.inventory = res.data?.data || [];
        this.inventoryPage = res.data?.current_page || 1;
        this.deviceTypeStats = res.data?.type_totals || [];
        this.inventoryTotalPages = res.data?.last_page || 1;
        this.inventoryTotal = res.data?.total || this.inventory.length;
      } catch (e) {
        console.error("Error fetching inventory:", e);
        this.inventory = [];
        this.inventoryTotal = 0;
        this.inventoryTotalPages = 1;
      } finally {
        this.$updateLoading(-1);
      }
    },

    async nextPage() {
      if (this.activeTab === "users") {
        if (this.usersPage < this.usersTotalPages) {
          this.usersPage++;
          await this.fetchUsers();
        }
        return;
      }

      if (this.inventoryPage < this.inventoryTotalPages) {
        this.inventoryPage++;
        await this.fetchInventory();
      }
    },

    async prevPage() {
      if (this.activeTab === "users") {
        if (this.usersPage > 1) {
          this.usersPage--;
          await this.fetchUsers();
        }
        return;
      }

      if (this.inventoryPage > 1) {
        this.inventoryPage--;
        await this.fetchInventory();
      }
    },

    formatMemory(bytes) {
      const b = Number(bytes || 0);
      if (!b) return '';

      const gb = b / 1024 / 1024 / 1024;
      return `${gb.toFixed(1)} GB`;
    },

    formatDt(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    clearChip(key) {
      if (key === "companyId") {
        this.companyId = null;
        this.users = [];
        this.inventory = [];
        this.usersTotal = 0;
        this.inventoryTotal = 0;
        this.usersPage = 1;
        this.inventoryPage = 1;
        return;
      }

      if (key === "users.status") this.filters.users.status = "all";
      if (key === "users.user_type") this.filters.users.user_type = "all";
      if (key === "inventory.status") this.filters.inventory.status = "all";
      if (key === "searchQuery") this.searchQuery = "";

      this.resetPageForActiveTab();
      this.fetchActiveTab();
    },

    async exportToCSV() {
      if (!this.companyId) return;

      const isUsers = this.activeTab === "users";
      this.$updateLoading(1);

      try {
        const params = isUsers
          ? {
            status: this.filters.users.status,
            user_type: this.filters.users.user_type,
            q: this.searchQuery || "",
            sort_by: this.sort.users.by,
            sort_dir: this.sort.users.dir,
          }
          : {
            status: this.filters.inventory.status,
            q: this.searchQuery || "",
            sort_by: this.sort.inventory.by,
            sort_dir: this.sort.inventory.dir,
          };

        const url = isUsers
          ? `requesters/export/${this.companyId}`
          : `clients/assets/export/${this.companyId}`;

        const res = await axios.get(url, { params });
        const rows = res.data?.data || [];

        const headers = isUsers
          ? (this.companyId != 30
            ? ["Name", "Email", "User Type", "Primary License", "Source", "Updated", "Active"]
            : ["Name", "Email", "User Type", "Source", "Updated", "Active"])
          : ["System", "Type", "OS", "Manufacturer", "Model", "BiosSerial", "Processor", "Memory", "Last User", "Location", "Last Update", "Active"];

        const data = rows.map(r => {
          if (isUsers) {
            const source = (r.azure_id && String(r.azure_id).trim() !== "") ? "Azure" : "Manual";
            const userType = `${r.user_type ?? ""}${r.user_type_overridden == 1 ? " (manual)" : ""}`;
            const base = [
              r.name || "",
              r.email || "",
              userType,
            ];

            if (this.companyId != 30) base.push(r.azure_license ?? "");
            base.push(source);
            base.push(this.formatDt(r.tmstmp));
            base.push(r.retired ? "Inactive" : "Active");
            return base;
          }

          return [
            r.system_name || "",
            r.node_class || "",
            r.os_name || "",
            r.system_manufacturer || "",
            r.system_model || "",
            r.bios_serial_number || "",
            r.processor_name || "",
            this.formatMemory(r.memory_capacity) || "",
            r.last_logged_in_user || "",
            r.location_name || "",
            r.last_update_at_ninja || "",
            r.active ? "Active" : "Inactive",
          ];
        });

        const csv = [headers, ...data]
          .map(line => line.map(v => `"${String(v ?? "").replaceAll('"', '""')}"`).join(","))
          .join("\r\n");

        const uri = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
        const link = document.createElement("a");
        link.href = uri;

        const tabName = isUsers ? "users" : "devices";
        link.download = `${tabName}_company_${this.companyId}_${new Date().toISOString().slice(0, 10)}.csv`;

        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        console.error("Export failed:", e);
        this.toast?.error?.("Export failed.");
      } finally {
        this.$updateLoading(-1);
      }
    },

    handleUserTypeChange(row, event) {
      const newValue = event.target.value;

      if (!newValue || newValue === row.user_type) {
        return;
      }

      this.pendingUserTypeRow = row;
      this.pendingUserTypeOldValue = row.user_type;
      this.pendingUserTypeValue = newValue;
      this.showUserTypeModal = true;
    },

    async confirmUserTypeOverride(permanent) {
      if (!this.pendingUserTypeRow || !this.pendingUserTypeValue) {
        this.closeUserTypeModal();
        return;
      }

      this.$updateLoading(1);

      try {
        const rowId = this.pendingUserTypeRow.id;

        const res = await axios.post(`requesters/user-type/${rowId}`, {
          user_type: this.pendingUserTypeValue,
          permanent: permanent ? 1 : 0,
        });

        const payload = res.data?.data || {};

        this.pendingUserTypeRow.user_type = payload.user_type || this.pendingUserTypeValue;
        this.pendingUserTypeRow.user_type_overridden = payload.user_type_overridden ?? 1;
        this.pendingUserTypeRow.user_type_override_permanent = payload.user_type_override_permanent ?? (permanent ? 1 : 0);

        this.toast?.success?.(
          `User type updated${permanent ? " (permanent override)." : "."}`
        );

        await this.fetchUsers();
      } catch (e) {
        console.error("Error updating user type:", e);
        this.toast?.error?.(
          e?.response?.data?.message || "Failed to update user type."
        );

        if (this.pendingUserTypeRow) {
          this.pendingUserTypeRow.user_type = this.pendingUserTypeOldValue;
        }
      } finally {
        this.closeUserTypeModal();
        this.$updateLoading(-1);
      }
    },

    cancelUserTypeOverride() {
      if (this.pendingUserTypeRow) {
        this.pendingUserTypeRow.user_type = this.pendingUserTypeOldValue;
      }

      this.closeUserTypeModal();
    },

    closeUserTypeModal() {
      this.showUserTypeModal = false;
      this.pendingUserTypeRow = null;
      this.pendingUserTypeValue = "";
      this.pendingUserTypeOldValue = "";
    }
  },

  mounted() {
    this.init();
  },
};
</script>

<style scoped>
th,
td {
  text-align: left;
}
</style>
